import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserCheck, Plus, Search, Edit2, Trash2,
  Mail, Calendar, AlertCircle, Users, ChevronDown, ArrowUpCircle, Settings, X, Lock
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserPartner } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Toast } from '../../components/Toast';
import { fetchCoachActivityStats } from '../../lib/partner-dashboard';
import type { CoachActivityStat } from '../../lib/partner-dashboard';

interface Coach {
  id: string;
  userId: string;
  name: string;
  email: string;
  tier: string;
  respondentLimit: number;
  teamsAccessEnabled: boolean;
  crmEnabled: boolean;
  aiAnalysisAccess: boolean;
  aiChatEnabled: boolean;
  aiVoiceEnabled: boolean;
  profileOnlyMode: boolean;
  maxAssessmentCodes: number | null;
  progressEnabled: boolean;
  pathwaysEnabled: boolean;
  presenceEnabled: boolean;
  promoteEnabled: boolean;
  profitEnabled: boolean;
  nlpPracticeEnabled: boolean;
  createdAt: Date;
  totalRespondents: number;
  completedAssessments: number;
  lastActiveDate: string | null;
}

interface Respondent {
  userId: string;
  name: string;
  email: string;
  coachName: string;
  coachId: string;
  completedAt: string | null;
}

export function PartnerCoachManagement() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'coaches' | 'respondents'>('coaches');
  const [isLoading, setIsLoading] = useState(true);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [respondentsLoaded, setRespondentsLoaded] = useState(false);
  const [respondentSearch, setRespondentSearch] = useState('');
  const [upgradeTarget, setUpgradeTarget] = useState<Respondent | null>(null);
  const [upgradeForm, setUpgradeForm] = useState({ tier: 'basic', respondentLimit: 25 });
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCoach, setEditingCoach] = useState<Coach | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [coachLimit, setCoachLimit] = useState(10);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    tier: 'basic',
    respondentLimit: 25
  });
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [updatingTierId, setUpdatingTierId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'totalRespondents' | 'completedAssessments' | null>(null);
  const [sortAsc, setSortAsc] = useState(false);

  // Set password modal
  const [passwordCoach, setPasswordCoach] = useState<Coach | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Feature settings drawer
  const [featureCoach, setFeatureCoach] = useState<Coach | null>(null);
  const [featureForm, setFeatureForm] = useState({
    aiChatEnabled: true,
    aiVoiceEnabled: true,
    profileOnlyMode: false,
    maxAssessmentCodes: '' as string | number,
    progressEnabled: true,
    pathwaysEnabled: true,
    presenceEnabled: true,
    promoteEnabled: true,
    profitEnabled: true,
    nlpPracticeEnabled: false,
  });
  const [isSavingFeatures, setIsSavingFeatures] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) {
          navigate('/login');
          return;
        }

        const { data: partner } = await supabase
          .from('partners')
          .select('id, max_coaches')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!partner) {
          navigate('/login');
          return;
        }

        setPartnerId(partner.id);
        setCoachLimit(partner.max_coaches || 10);
        await loadCoaches(partner.id);
      } catch (err) {
        console.error('Error checking partner status:', err);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadCoaches = async (pId: string) => {
    try {
      const [coachesData, activityStats] = await Promise.all([
        supabase
          .from('coaches')
          .select('id, user_id, tier, max_respondents, teams_access_enabled, crm_enabled, ai_analysis_access, ai_chat_enabled, ai_voice_enabled, profile_only_mode, max_assessment_codes, progress_enabled, pathways_enabled, presence_enabled, promote_enabled, profit_enabled, nlp_practice_enabled, created_at, users!coaches_user_id_fkey(full_name, email)')
          .eq('partner_id', pId)
          .then(r => r.data as any[]),
        fetchCoachActivityStats(pId),
      ]);

      const statsById: Record<string, CoachActivityStat> = {};
      activityStats.forEach(s => { statsById[s.coachId] = s; });

      const mapped = (coachesData || []).map(c => ({
        id: c.id,
        userId: c.user_id,
        name: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
        email: (c.users as any)?.email || '',
        tier: c.tier || 'basic',
        respondentLimit: c.max_respondents || 25,
        teamsAccessEnabled: c.teams_access_enabled || false,
        crmEnabled: c.crm_enabled || false,
        aiAnalysisAccess: c.ai_analysis_access || false,
        aiChatEnabled: c.ai_chat_enabled !== false,
        aiVoiceEnabled: c.ai_voice_enabled !== false,
        profileOnlyMode: c.profile_only_mode || false,
        maxAssessmentCodes: c.max_assessment_codes ?? null,
        progressEnabled: c.progress_enabled !== false,
        pathwaysEnabled: c.pathways_enabled !== false,
        presenceEnabled: c.presence_enabled !== false,
        promoteEnabled: c.promote_enabled !== false,
        profitEnabled: c.profit_enabled !== false,
        nlpPracticeEnabled: c.nlp_practice_enabled || false,
        createdAt: new Date(c.created_at || Date.now()),
        totalRespondents: statsById[c.id]?.totalRespondents || 0,
        completedAssessments: statsById[c.id]?.completedAssessments || 0,
        lastActiveDate: statsById[c.id]?.lastActiveDate || null,
      })) as Coach[];

      setCoaches(mapped);
    } catch (err) {
      console.error('Error loading coaches:', err);
      setError('Failed to load coaches');
    }
  };

  const loadRespondents = async (pId: string) => {
    try {
      const { data: coachRows } = await supabase
        .from('coaches')
        .select('id, users!coaches_user_id_fkey(full_name, email)')
        .eq('partner_id', pId);

      if (!coachRows || coachRows.length === 0) {
        setRespondents([]);
        setRespondentsLoaded(true);
        return;
      }

      const coachIds = coachRows.map(c => c.id);
      const coachNameById: Record<string, string> = {};
      coachRows.forEach(c => {
        coachNameById[c.id] = (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown';
      });

      const { data: respondentRows } = await supabase
        .from('respondents')
        .select('user_id, coach_id, last_assessment_date, users!respondents_user_id_fkey(full_name, email)')
        .in('coach_id', coachIds);

      const existing = new Set(coachRows.map(c => (c.users as any)?.email?.toLowerCase()));

      const mapped: Respondent[] = (respondentRows || [])
        .filter(r => {
          if (!r.coach_id) return false;
          const email = (r.users as any)?.email?.toLowerCase();
          return email && !existing.has(email);
        })
        .map(r => ({
          userId: r.user_id,
          name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
          email: (r.users as any)?.email || '',
          coachName: coachNameById[r.coach_id!] || 'Unknown',
          coachId: r.coach_id!,
          completedAt: r.last_assessment_date || null,
        }));

      // Deduplicate by userId
      const seen = new Set<string>();
      const deduped = mapped.filter(r => {
        if (seen.has(r.userId)) return false;
        seen.add(r.userId);
        return true;
      });

      setRespondents(deduped);
      setRespondentsLoaded(true);
    } catch (err) {
      console.error('Error loading respondents:', err);
    }
  };

  const handleUpgradeToCoach = async () => {
    if (!upgradeTarget || !partnerId || isUpgrading) return;
    setIsUpgrading(true);
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('upgrade-to-coach', {
        body: {
          targetUserId: upgradeTarget.userId,
          tier: upgradeForm.tier,
          maxRespondents: upgradeForm.respondentLimit,
        },
      });

      if (fnError || result?.error) {
        let msg = 'Failed to upgrade user';
        try {
          const body = await (fnError as any)?.context?.json?.();
          if (body?.error) msg = body.error;
        } catch {
          if (result?.error) msg = result.error;
        }
        throw new Error(msg);
      }

      setUpgradeTarget(null);
      await Promise.all([loadCoaches(partnerId), loadRespondents(partnerId)]);
      const emailSent = result?.emailSent === true;
      setSuccessMessage(
        emailSent
          ? `${upgradeTarget.name} has been upgraded to coach. A notification email has been sent to them.`
          : `${upgradeTarget.name} has been upgraded to coach. Email could not be sent — please notify them manually.`
      );
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to upgrade user');
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setModalError(null);

    if (!partnerId || isSaving) return;

    if (!editingCoach && coaches.length >= coachLimit) {
      setModalError(`You have reached your coach limit (${coachLimit})`);
      return;
    }

    setIsSaving(true);
    let emailSent = false;
    try {
      if (editingCoach) {
        const newTier = formData.tier as 'free' | 'basic' | 'basic_plus' | 'advanced' | 'partner';
        const tierChanged = editingCoach.tier !== newTier;
        const updatePayload: Record<string, any> = {
          tier: newTier,
          max_respondents: formData.respondentLimit
        };
        if (tierChanged) {
          updatePayload.ai_analysis_access = ['advanced', 'partner'].includes(newTier);
        }

        await supabase
          .from('coaches')
          .update(updatePayload)
          .eq('id', editingCoach.id);

        await supabase
          .from('users')
          .update({ full_name: formData.name })
          .eq('id', editingCoach.userId);
      } else {
        const assessmentCode = 'HPOS' + crypto.randomUUID().slice(0, 6).toUpperCase();
        const { data: result, error: fnError } = await supabase.functions.invoke('create-coach', {
          body: {
            email: formData.email,
            password: formData.password,
            fullName: formData.name,
            tier: formData.tier,
            assessmentCode,
            partnerId,
            maxRespondents: formData.respondentLimit,
          },
        });

        if (fnError) {
          let errMsg = 'Failed to create coach account';
          try {
            const errBody = await (fnError as any).context?.json?.();
            if (errBody?.error) errMsg = errBody.error;
          } catch {
            if (result?.error) errMsg = result.error;
          }
          throw new Error(errMsg);
        }

        emailSent = result?.emailSent === true;
      }

      const wasAdding = !editingCoach;
      await loadCoaches(partnerId);
      setShowAddModal(false);
      setEditingCoach(null);
      setModalError(null);
      setFormData({ name: '', email: '', password: '', tier: 'basic', respondentLimit: 25 });

      if (wasAdding) {
        setSuccessMessage(
          emailSent
            ? 'Coach account created successfully! Login details have been emailed to the coach.'
            : 'Coach account created successfully. Email could not be sent -- please share login details manually.'
        );
      }
    } catch (err: any) {
      console.error('Error saving coach:', err);
      setModalError(err.message || 'Failed to save coach');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (coachId: string) => {
    if (!confirm('Are you sure you want to delete this coach?')) return;
    if (!partnerId) return;

    try {
      const { error: deleteError } = await supabase
        .from('coaches')
        .delete()
        .eq('id', coachId);

      if (deleteError) throw deleteError;

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error deleting coach:', err);
      setError('Failed to delete coach');
    }
  };

  const toggleTeamsAccess = async (coach: Coach) => {
    if (!partnerId) return;
    try {
      await supabase
        .from('coaches')
        .update({ teams_access_enabled: !coach.teamsAccessEnabled })
        .eq('id', coach.id);

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error toggling teams access:', err);
      setError('Failed to update teams access');
    }
  };

  const toggleCRMAccess = async (coach: Coach) => {
    if (!partnerId) return;
    try {
      await supabase
        .from('coaches')
        .update({ crm_enabled: !coach.crmEnabled })
        .eq('id', coach.id);

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error toggling CRM access:', err);
      setError('Failed to update CRM access');
    }
  };

  const toggleAIAnalysis = async (coach: Coach) => {
    if (!partnerId) return;
    try {
      await supabase
        .from('coaches')
        .update({ ai_analysis_access: !coach.aiAnalysisAccess })
        .eq('id', coach.id);

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error toggling AI analysis access:', err);
      setError('Failed to update AI analysis access');
    }
  };

  const updateCoachTier = async (coach: Coach, newTier: string) => {
    if (!partnerId) return;
    setUpdatingTierId(coach.id);
    try {
      const updatePayload: Record<string, any> = {
        tier: newTier as 'free' | 'basic' | 'basic_plus' | 'advanced' | 'partner',
        ai_analysis_access: ['advanced', 'partner'].includes(newTier),
      };
      // When switching to free tier, auto-enable profile-only mode
      if (newTier === 'free') {
        updatePayload.profile_only_mode = true;
      }
      // When upgrading away from free, clear profile-only mode
      if (coach.tier === 'free' && newTier !== 'free') {
        updatePayload.profile_only_mode = false;
      }

      await supabase
        .from('coaches')
        .update(updatePayload as any)
        .eq('id', coach.id);

      await loadCoaches(partnerId);
      setSuccessMessage(`${coach.name}'s tier updated to ${newTier.replace('_', ' ')}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating tier:', err);
      setError('Failed to update tier');
    } finally {
      setUpdatingTierId(null);
    }
  };

  const openFeatureSettings = (coach: Coach) => {
    setFeatureCoach(coach);
    setFeatureForm({
      aiChatEnabled: coach.aiChatEnabled,
      aiVoiceEnabled: coach.aiVoiceEnabled,
      profileOnlyMode: coach.profileOnlyMode,
      maxAssessmentCodes: coach.maxAssessmentCodes ?? '',
      progressEnabled: coach.progressEnabled,
      pathwaysEnabled: coach.pathwaysEnabled,
      presenceEnabled: coach.presenceEnabled,
      promoteEnabled: coach.promoteEnabled,
      profitEnabled: coach.profitEnabled,
      nlpPracticeEnabled: coach.nlpPracticeEnabled,
    });
  };

  const saveFeatureSettings = async () => {
    if (!featureCoach || !partnerId || isSavingFeatures) return;
    setIsSavingFeatures(true);
    try {
      const maxCodes = featureForm.maxAssessmentCodes === '' ? null : Number(featureForm.maxAssessmentCodes);
      await (supabase
        .from('coaches')
        .update({
          ai_chat_enabled: featureForm.aiChatEnabled,
          ai_voice_enabled: featureForm.aiVoiceEnabled,
          profile_only_mode: featureForm.profileOnlyMode,
          max_assessment_codes: maxCodes,
          progress_enabled: featureForm.progressEnabled,
          pathways_enabled: featureForm.pathwaysEnabled,
          presence_enabled: featureForm.presenceEnabled,
          promote_enabled: featureForm.promoteEnabled,
          profit_enabled: featureForm.profitEnabled,
          nlp_practice_enabled: featureForm.nlpPracticeEnabled,
        } as any)
        .eq('id', featureCoach.id));

      await loadCoaches(partnerId);
      setFeatureCoach(null);
      setSuccessMessage(`Feature settings updated for ${featureCoach.name}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving feature settings:', err);
      setError('Failed to save feature settings');
    } finally {
      setIsSavingFeatures(false);
    }
  };

  const openPasswordModal = (coach: Coach) => {
    setPasswordCoach(coach);
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordError(null);
  };

  const handleSetPassword = async () => {
    if (!passwordCoach) return;
    setPasswordError(null);

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setIsSavingPassword(true);
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('set-coach-password', {
        body: {
          targetUserId: passwordCoach.userId,
          newPassword,
        },
      });

      if (fnError) {
        let errMsg = 'Failed to update password';
        try {
          const errBody = await (fnError as any).context?.json?.();
          if (errBody?.error) errMsg = errBody.error;
        } catch {
          if (result?.error) errMsg = result.error;
        }
        throw new Error(errMsg);
      }

      setPasswordCoach(null);
      setSuccessMessage(`Password updated for ${passwordCoach.name}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to update password');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const resendCredentials = async (coach: Coach) => {
    if (!confirm(`This will reset ${coach.name}'s password and send them a new login email. Continue?`)) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'resend-credentials-coach',
            recipientEmail: coach.email,
            recipientName: coach.name,
            userId: coach.userId,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to send email');
      setSuccessMessage(`Login details resent to ${coach.email}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend credentials');
    }
  };

  const openEditModal = (coach: Coach) => {
    setEditingCoach(coach);
    setFormData({
      name: coach.name,
      email: coach.email,
      password: '',
      tier: coach.tier,
      respondentLimit: coach.respondentLimit
    });
    setShowAddModal(true);
  };

  const filteredCoaches = coaches
    .filter(coach =>
      coach.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coach.email.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (!sortField) return 0;
      const diff = a[sortField] - b[sortField];
      return sortAsc ? diff : -diff;
    });

  const filteredRespondents = respondents.filter(r =>
    r.name.toLowerCase().includes(respondentSearch.toLowerCase()) ||
    r.email.toLowerCase().includes(respondentSearch.toLowerCase()) ||
    r.coachName.toLowerCase().includes(respondentSearch.toLowerCase())
  );

  const handleTabChange = (tab: 'coaches' | 'respondents') => {
    setActiveTab(tab);
    if (tab === 'respondents' && !respondentsLoaded && partnerId) {
      loadRespondents(partnerId);
    }
  };

  const handleSort = (field: 'totalRespondents' | 'completedAssessments') => {
    if (sortField === field) {
      setSortAsc(prev => !prev);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  const SortIcon = ({ field }: { field: 'totalRespondents' | 'completedAssessments' }) => {
    if (sortField !== field) return <ChevronDown className="w-3.5 h-3.5 text-chocolate-300 ml-1" />;
    return <ChevronDown className={`w-3.5 h-3.5 text-sage-600 ml-1 transition-transform ${sortAsc ? 'rotate-180' : ''}`} />;
  };

  const tierBadgeColors: Record<string, string> = {
    free: 'bg-cream-100 text-chocolate-500 border border-cream-300',
    basic: 'bg-cream-200 text-chocolate-700',
    basic_plus: 'bg-sage-100 text-sage-700',
    advanced: 'bg-coral-100 text-coral-700',
    partner: 'bg-warm-100 text-warm-700'
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading coach management..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-chocolate-900">Coach Management</h1>
                <p className="text-sm text-chocolate-500">{coaches.length} / {coachLimit} coaches</p>
              </div>
            </div>
            {activeTab === 'coaches' && (
              <button
                onClick={() => {
                  setEditingCoach(null);
                  setError(null);
                  setModalError(null);
                  setFormData({ name: '', email: '', password: '', tier: 'basic', respondentLimit: 25 });
                  setShowAddModal(true);
                }}
                disabled={coaches.length >= coachLimit}
                className="freud-btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-5 h-5" />
                Add Coach
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 p-1 rounded-xl bg-cream-100 w-fit">
            <button
              onClick={() => handleTabChange('coaches')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'coaches' ? 'bg-white text-chocolate-900 shadow-soft' : 'text-chocolate-500 hover:text-chocolate-700'}`}
            >
              Coaches
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full font-bold bg-sage-100 text-sage-700">{coaches.length}</span>
            </button>
            <button
              onClick={() => handleTabChange('respondents')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'respondents' ? 'bg-white text-chocolate-900 shadow-soft' : 'text-chocolate-500 hover:text-chocolate-700'}`}
            >
              <ArrowUpCircle className="w-4 h-4" />
              Upgrade Respondent
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* ── Respondents tab ─────────────────────────────────────── */}
        {activeTab === 'respondents' && (
          <div className="freud-card-elevated mb-6">
            <div className="p-4 border-b border-cream-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-chocolate-700">Upgrade a respondent to a coach</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Only respondents who are not already coaches are shown.</p>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
                <input
                  type="text"
                  placeholder="Search respondents..."
                  autoComplete="off"
                  name="respondent-search"
                  value={respondentSearch}
                  onChange={(e) => setRespondentSearch(e.target.value)}
                  className="freud-input pl-10"
                />
              </div>
            </div>

            {!respondentsLoaded ? (
              <div className="p-12 text-center text-chocolate-400">Loading respondents…</div>
            ) : filteredRespondents.length === 0 ? (
              <div className="p-12 text-center text-chocolate-400">
                {respondents.length === 0 ? 'No eligible respondents found.' : 'No respondents match your search.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-cream-100">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Name</th>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Email</th>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Current Coach</th>
                      <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Completed</th>
                      <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cream-200">
                    {filteredRespondents.map(r => (
                      <tr key={r.userId} className="hover:bg-cream-50">
                        <td className="px-6 py-4 font-medium text-chocolate-900">{r.name}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-chocolate-600">
                            <Mail className="w-4 h-4" />
                            {r.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-chocolate-600">{r.coachName}</td>
                        <td className="px-6 py-4 text-chocolate-500 text-sm">
                          {r.completedAt ? new Date(r.completedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => {
                              setUpgradeTarget(r);
                              setUpgradeForm({ tier: 'basic', respondentLimit: 25 });
                              setError(null);
                            }}
                            disabled={coaches.length >= coachLimit}
                            className="freud-btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
                            title={coaches.length >= coachLimit ? `Coach limit reached (${coachLimit})` : 'Upgrade to coach'}
                          >
                            <ArrowUpCircle className="w-4 h-4" />
                            Upgrade to Coach
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'coaches' && (
        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Search coaches..."
                autoComplete="off"
                name="coach-search"
                data-lpignore="true"
                data-1p-ignore="true"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Coach</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Email</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Tier</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Limit</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700 cursor-pointer select-none" onClick={() => handleSort('totalRespondents')}>
                    <span className="inline-flex items-center">Respondents <SortIcon field="totalRespondents" /></span>
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700 cursor-pointer select-none" onClick={() => handleSort('completedAssessments')}>
                    <span className="inline-flex items-center">Completions <SortIcon field="completedAssessments" /></span>
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Last Active</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">Teams</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">CRM</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">AI Analysis</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">AI Chat</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">AI Voice</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Created</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredCoaches.map((coach) => (
                  <tr key={coach.id} className="hover:bg-cream-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-chocolate-900">{coach.name}</span>
                      {coach.userId === user?.id && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warm-100 text-warm-700 uppercase">You</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Mail className="w-4 h-4" />
                        {coach.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={coach.tier}
                        onChange={(e) => updateCoachTier(coach, e.target.value)}
                        disabled={updatingTierId === coach.id}
                        className={`px-2 py-1 rounded-full text-xs border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sage-400 disabled:opacity-50 ${tierBadgeColors[coach.tier] || 'bg-cream-200 text-chocolate-700'}`}
                      >
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="basic_plus">Basic Plus</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Users className="w-4 h-4" />
                        {coach.respondentLimit}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                        coach.totalRespondents === 0
                          ? 'bg-cream-200 text-chocolate-400'
                          : 'bg-sage-100 text-sage-800'
                      }`}>
                        {coach.totalRespondents}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                        coach.completedAssessments === 0
                          ? 'bg-cream-200 text-chocolate-400'
                          : 'bg-warm-100 text-warm-800'
                      }`}>
                        {coach.completedAssessments}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {coach.lastActiveDate ? (
                        <div className="flex items-center gap-2 text-chocolate-600 text-sm">
                          <Calendar className="w-4 h-4" />
                          {new Date(coach.lastActiveDate).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-chocolate-300 text-sm">Never</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleTeamsAccess(coach); }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.teamsAccessEnabled ? 'bg-sage-500' : 'bg-cream-300'
                        }`}
                        title={coach.teamsAccessEnabled ? 'Teams enabled' : 'Teams disabled'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          coach.teamsAccessEnabled ? 'translate-x-4.5' : 'translate-x-1'
                        }`} style={{ transform: `translateX(${coach.teamsAccessEnabled ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleCRMAccess(coach); }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.crmEnabled ? 'bg-blue-500' : 'bg-cream-300'
                        }`}
                        title={coach.crmEnabled ? 'CRM enabled' : 'CRM disabled'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`} style={{ transform: `translateX(${coach.crmEnabled ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleAIAnalysis(coach); }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.aiAnalysisAccess ? 'bg-amber-500' : 'bg-cream-300'
                        }`}
                        title={coach.aiAnalysisAccess ? 'AI Analysis enabled' : 'AI Analysis disabled'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`} style={{ transform: `translateX(${coach.aiAnalysisAccess ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!partnerId) return;
                          await supabase.from('coaches').update({ ai_chat_enabled: !coach.aiChatEnabled } as any).eq('id', coach.id);
                          await loadCoaches(partnerId);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.aiChatEnabled ? 'bg-sage-500' : 'bg-cream-300'
                        }`}
                        title={coach.aiChatEnabled ? 'AI Chat enabled' : 'AI Chat disabled'}
                      >
                        <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${coach.aiChatEnabled ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!partnerId) return;
                          await supabase.from('coaches').update({ ai_voice_enabled: !coach.aiVoiceEnabled } as any).eq('id', coach.id);
                          await loadCoaches(partnerId);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.aiVoiceEnabled ? 'bg-sage-500' : 'bg-cream-300'
                        }`}
                        title={coach.aiVoiceEnabled ? 'AI Voice enabled' : 'AI Voice disabled'}
                      >
                        <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${coach.aiVoiceEnabled ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Calendar className="w-4 h-4" />
                        {coach.createdAt.toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openFeatureSettings(coach)}
                          className="p-2 text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"
                          title="Feature settings"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openPasswordModal(coach)}
                          className="p-2 text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"
                          title="Set password"
                        >
                          <Lock className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => resendCredentials(coach)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Resend login details"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(coach)}
                          className="p-2 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(coach.id)}
                          className="p-2 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCoaches.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              No coaches found
            </div>
          )}
        </div>
        )} {/* end activeTab === 'coaches' */}
      </main>

      <Toast
        isVisible={!!successMessage}
        message={successMessage || ''}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Upgrade to Coach modal */}
      {upgradeTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2.5 bg-sage-100 rounded-xl">
                <ArrowUpCircle className="w-6 h-6 text-sage-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-chocolate-900">Upgrade to Coach</h2>
                <p className="text-sm text-chocolate-500">{upgradeTarget.name} · {upgradeTarget.email}</p>
              </div>
            </div>
            <p className="text-sm text-chocolate-600 mb-5 bg-cream-100 rounded-lg p-3">
              This will convert the existing respondent account into a coach account. The user will keep their existing login credentials and can access the coach dashboard immediately.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Coach Tier</label>
                <select
                  value={upgradeForm.tier}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, tier: e.target.value })}
                  className="freud-input"
                >
                  <option value="free">Free</option>
                  <option value="basic">Basic</option>
                  <option value="basic_plus">Basic Plus</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Respondent Limit</label>
                <input
                  type="number"
                  value={upgradeForm.respondentLimit}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, respondentLimit: parseInt(e.target.value) })}
                  min="1"
                  className="freud-input"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-5">
              <button
                type="button"
                onClick={() => setUpgradeTarget(null)}
                className="freud-btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleUpgradeToCoach}
                disabled={isUpgrading}
                className="freud-btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpgrading ? 'Upgrading…' : 'Confirm Upgrade'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <h2 className="text-xl font-bold text-chocolate-900 mb-6">
              {editingCoach ? 'Edit Coach' : 'Add Coach'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="freud-input"
                />
              </div>
              {!editingCoach && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="freud-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                      name="new-coach-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      className="freud-input"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Tier
                </label>
                <select
                  value={formData.tier}
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                  className="freud-input"
                >
                  <option value="free">Free</option>
                  <option value="basic">Basic</option>
                  <option value="basic_plus">Basic Plus</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Respondent Limit
                </label>
                <input
                  type="number"
                  value={formData.respondentLimit}
                  onChange={(e) => setFormData({ ...formData, respondentLimit: parseInt(e.target.value) })}
                  min="1"
                  required
                  className="freud-input"
                />
              </div>
              {modalError && (
                <div className="bg-coral-100 border border-coral-200 text-coral-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {modalError}
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCoach(null);
                    setModalError(null);
                  }}
                  className="freud-btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="freud-btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : editingCoach ? 'Save Changes' : 'Add Coach'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      {/* Feature Settings Modal */}
      {featureCoach && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-sage-100 rounded-xl">
                  <Settings className="w-5 h-5 text-sage-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-chocolate-900">Feature Settings</h2>
                  <p className="text-xs text-chocolate-500">{featureCoach.name}</p>
                </div>
              </div>
              <button
                onClick={() => setFeatureCoach(null)}
                className="p-2 text-chocolate-400 hover:bg-cream-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Profile Only Mode */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">Profile Only Mode</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Only shows Respondents & Assessment Codes. All other features are hidden.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, profileOnlyMode: !f.profileOnlyMode }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.profileOnlyMode ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.profileOnlyMode ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* Assessment Code Limit */}
              <div className="p-3 rounded-lg bg-cream-50 border border-cream-200">
                <label className="block text-sm font-semibold text-chocolate-800 mb-1">Assessment Code Limit</label>
                <p className="text-xs text-chocolate-400 mb-2">Maximum total codes this coach can create. Leave blank for unlimited.</p>
                <input
                  type="number"
                  min="1"
                  value={featureForm.maxAssessmentCodes}
                  onChange={(e) => setFeatureForm(f => ({ ...f, maxAssessmentCodes: e.target.value }))}
                  placeholder="Unlimited"
                  className="freud-input text-sm"
                />
              </div>

              {/* Progress */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">Progress</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Client Progress & Transformation Reports.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, progressEnabled: !f.progressEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.progressEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.progressEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* Pathways */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">Pathways</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Transformation Pathways & Habit Installation OS.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, pathwaysEnabled: !f.pathwaysEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.pathwaysEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.pathwaysEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* Presence */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">Presence</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Coaching Sessions & Session Intelligence.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, presenceEnabled: !f.presenceEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.presenceEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.presenceEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* Promote */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">Promote</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Lead Page & Sales Pipeline.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, promoteEnabled: !f.promoteEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.promoteEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.promoteEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* Profit */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">Profit</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Profit Center dashboard.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, profitEnabled: !f.profitEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.profitEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.profitEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* AI Chat */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">AI Chat</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Enable AI chat for this coach and their clients.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, aiChatEnabled: !f.aiChatEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.aiChatEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.aiChatEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* AI Voice */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">AI Voice</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Enable AI voice for this coach and their clients.</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, aiVoiceEnabled: !f.aiVoiceEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.aiVoiceEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.aiVoiceEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>

              {/* NLP Practice */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div>
                  <p className="text-sm font-semibold text-chocolate-800">NLP Practice</p>
                  <p className="text-xs text-chocolate-400 mt-0.5">Enable NLP Practice Coach (Meta Model, Milton Model, Presuppositions, and Trainer AI).</p>
                </div>
                <button
                  onClick={() => setFeatureForm(f => ({ ...f, nlpPracticeEnabled: !f.nlpPracticeEnabled }))}
                  className={`relative flex-shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${featureForm.nlpPracticeEnabled ? 'bg-sage-500' : 'bg-cream-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white" style={{ transform: `translateX(${featureForm.nlpPracticeEnabled ? '18px' : '2px'})` }} />
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-5">
              <button
                onClick={() => setFeatureCoach(null)}
                className="freud-btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={saveFeatureSettings}
                disabled={isSavingFeatures}
                className="freud-btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingFeatures ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Set Password Modal */}
      {passwordCoach && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-sage-100 rounded-xl">
                  <Lock className="w-5 h-5 text-sage-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-chocolate-900">Set Password</h2>
                  <p className="text-xs text-chocolate-500">{passwordCoach.name} · {passwordCoach.email}</p>
                </div>
              </div>
              <button
                onClick={() => setPasswordCoach(null)}
                className="p-2 text-chocolate-400 hover:bg-cream-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-chocolate-600 mb-4 bg-cream-100 rounded-lg p-3">
              This will immediately replace the coach's current password. They will need to use the new password the next time they sign in.
            </p>

            <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSetPassword(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  name="coach-new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  className="freud-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Re-enter the new password"
                  autoComplete="new-password"
                  name="coach-confirm-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  className="freud-input"
                />
              </div>
              {passwordError && (
                <div className="bg-coral-100 border border-coral-200 text-coral-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {passwordError}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setPasswordCoach(null)}
                  className="freud-btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetPassword}
                  disabled={isSavingPassword || !newPassword || !confirmNewPassword}
                  className="freud-btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingPassword ? 'Updating…' : 'Update Password'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
